import { useCallback, useState } from 'react';

import { ArrowDownTrayIcon as DownloadIcon } from '@heroicons/react/24/outline';

import { AxiosError, AxiosResponse } from 'axios';

import adminAPI from '@/api/admin';
import filesAPI from '@/api/files';
import ordersAPI from '@/api/ordersV2';
import { ErrorAlert, SuccessAlert } from '@/components/Alert';
import FileUploadComponent from '@/components/FileUploadComponent';
import Spinner from '@/components/Icons/Spinner';
import InputCheckbox from '@/components/InputCheckbox';
import LoadingAnimation from '@/components/LoadingAnimation';
import Modal from '@/components/Modals/Modal';
import ModalNoSuccess from '@/components/Modals/ModalNoSuccess';
import Search from '@/components/Search';
import TextAreaMessage from '@/components/TextAreaMessage';
import MESSAGES from '@/constants/messages-en';
import useAuth from '@/hooks/useAuth';
import IOrder, { OrderOrigin, OrderPausedBy, OrderTag } from '@/types/IOrderV2';
import IOrganisation from '@/types/IOrganisation';

import OrderDetails from '../OrderDetails/OrderDetails';
import TableData from '../shared/TableData';
import OrderTableData from './TableData';

export interface IOrdersListItem {
  id: number;
  url?: string;
  uuid: string;
  orderUrl: string;
  customer_name: string;
  customer_email: string;
  designFile?: any;
  selected: boolean;
  activationId: number | undefined;
  orderType: OrderOrigin | undefined;
  orderNumber: string;
  numberOfProfiles: number;
  orderStatus: string;
  orgId: number;
  notifications_sent?: string;
  submission_date: string;
  is_priority?: boolean;
  alternate_email?: string;
  alternate_email_flag: boolean;
  shipping_country: string | null;
  handleCheckedItem: () => void;
  handleOrderStatusChange: () => void;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>, order_id: number) => void;
  tag: OrderTag;
  paused_by?: OrderPausedBy;
  handleTabColor: () => void;
  setSuccess: React.Dispatch<React.SetStateAction<string>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  inserted_at: string;
  has_qr_codes: boolean;
  order: IOrder;
}

export default function OrdersListItem({
  id,
  uuid,
  orderUrl,
  customer_name,
  customer_email,
  designFile,
  selected,
  orderType,
  orderNumber,
  numberOfProfiles,
  orderStatus,
  orgId,
  notifications_sent,
  submission_date,
  is_priority,
  alternate_email,
  alternate_email_flag,
  shipping_country,
  handleCheckedItem,
  handleOrderStatusChange,
  setSuccess,
  setError,
  onDragStart,
  tag,
  paused_by,
  inserted_at,
  has_qr_codes,
  order,
}: IOrdersListItem) {
  const { user } = useAuth();

  const [isMoveOrder, setIsMoveOrder] = useState(false);
  const [isDropFile, setIsDropFile] = useState(false);
  const [isChangeRequest, setIsChangeRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [orgDataModal, setOrgDataModal] = useState<IOrganisation[]>();
  const [orgModalSearch, setOrgModalSearch] = useState('');
  const [showOptionsModal, setShowOptionsModal] = useState(false);

  const [orgIDModal, setOrgIDModal] = useState(-1);
  const [file, setFile] = useState<File | undefined>();
  const [designSizeError, setDesignSizeError] = useState(false);
  const [designFileError, setDesignFileError] = useState(false);
  const [designLoading, setDesignLoading] = useState(false);
  const [FileID, setFileID] = useState(-1);
  const [checkbox, setCheckbox] = useState(false);
  const [isOpenOrderDetailModal, setIsOpenOrderDetailModal] = useState(false);
  const [isPriority, setIsPriority] = useState(!!is_priority);

  const [changeRequest, setChangeRequest] = useState('');
  const [changeRequestFile, setChangeRequestFile] = useState<any>();
  const [downloading, setDownloading] = useState(false);

  const [
    successDownloadChangeRequestFile,
    setSuccessDownloadChangeRequestFile,
  ] = useState('');
  const [errorDownloadChangeRequestFile, setErrorDownloadChangeRequestFile] =
    useState(false);

  const listOrganisationsModal = useCallback(
    async (newOrgModalSearch: string) => {
      if (newOrgModalSearch) {
        const res = await adminAPI.listOrganisations({
          pageSize: 5,
          search: newOrgModalSearch,
        });
        setOrgDataModal(
          res.data?.data?.filter((item: IOrganisation) => item.name),
        );

        return res.data;
      }
      return {
        data: [],
        paging: {
          page_number: 1,
          page_offset: 5,
          page_size: 5,
          total_entries: 0,
          total_pages: 1,
        },
      };
    },
    [],
  );

  const handleMoveOrg = useCallback(
    async (order_id: number, organisation_id: number) => {
      if (organisation_id <= 0) {
        return;
      }
      setIsLoading(true);
      try {
        const rep = await ordersAPI.assignToDifferentOrg({
          order_id,
          organisation_id,
        });

        if (rep) {
          setSuccess('Order Moved');
          handleOrderStatusChange();
        } else {
          setError(true);
        }
      } catch (error) {
        setError(true);
      } finally {
        setIsMoveOrder(false);
        setIsLoading(false);
      }
    },
    [handleOrderStatusChange, setError, setSuccess],
  );

  const handleClickLinkedOrg = (orgId: number) => {
    if (!orgId) {
      setError(true);
      return;
    }
    window.open(`/?scope=user&uOrgID=${orgId}`);
  };

  return (
    <div
      onDragStart={e => {
        if (onDragStart) {
          onDragStart(e, id);
        }
      }}
      draggable
    >
      <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 border-b ">
        <tbody>
          <tr className="bg-white">
            <>
              <th scope="col" className="py-7 w-5.3%">
                <InputCheckbox
                  id={`Checkbox-${id}}`}
                  label=""
                  value={selected}
                  onChange={handleCheckedItem}
                />
              </th>
              {OrderTableData({
                orderStatus,
                is_priority,
                setIsOpenOrderDetailModal,
                orderNumber,
                numberOfProfiles,
                tag,
                customer_name,
                customer_email,
                shipping_country,
                orderType,
                setChangeRequestFile,
                setChangeRequest,
                setIsChangeRequest,
                notifications_sent,
                submission_date,
                paused_by,
                orderUrl,
                setError,
                setSuccess,
                setIsLoading,
                handleOrderStatusChange,
                id,
                designFile,
                setDownloading,
                downloading,
                setIsDropFile,
                orgId,
                setIsMoveOrder,
                uuid,
                setIsPriority,
                isPriority,
                inserted_at,
                has_qr_codes,
              }).map((item, index) => {
                return (
                  <TableData
                    key={index}
                    style={item.style}
                    className={item.className}
                  >
                    {item.children}
                  </TableData>
                );
              })}
            </>
          </tr>
        </tbody>
      </table>
      {isLoading && (
        <div className="absolute text-gray-500 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <LoadingAnimation className="w-16 h-16 mx-auto text-brand-500" />
        </div>
      )}
      {/* Search Modal */}
      <Modal
        isOpen={isMoveOrder}
        setIsOpen={setIsMoveOrder}
        dialogTitle="Move Order and Profiles"
        dialogDescription="This will move the order and profiles associated with the order to the selected organisation"
        successButtonText="Move profiles"
        onSuccess={() => {
          handleMoveOrg(id, orgIDModal);
        }}
        onCancel={() => {
          setOrgModalSearch('');
          setOrgIDModal(-1);
        }}
        isLoading={isLoading}
      >
        <div className="mt-6">
          <div className="mt-6 relative">
            <Search
              id={`OrgList-1-20-date-desc--${orgModalSearch}`}
              placeholder="Search for Organisation ID or Name"
              search={orgModalSearch}
              setSearch={value => {
                if (value !== orgModalSearch) {
                  setOrgIDModal(-1);
                }

                if (value && orgIDModal < 0) {
                  setShowOptionsModal(true);
                  setOrgModalSearch(value);
                } else {
                  setShowOptionsModal(false);
                }
              }}
              fetchQuery={newOrgModalSearch =>
                listOrganisationsModal(newOrgModalSearch)
              }
            />

            {orgModalSearch && showOptionsModal && (
              <div className="origin-top-left absolute right-0 mt-2 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                <div className="py-1">
                  {orgDataModal && orgDataModal?.length ? (
                    orgDataModal?.map((item: IOrganisation) => (
                      <button
                        type="button"
                        key={item.id}
                        className="appearance-none px-3 py-2 cursor-pointer hover:bg-gray-200 w-full text-left"
                        onClick={() => {
                          setOrgIDModal(item.id);
                          setShowOptionsModal(false);
                          setOrgModalSearch(`#${item.id} ${item.name}`);
                        }}
                      >
                        #{item.id}{' '}
                        <span className="text-gray-500">{item.name}</span>
                      </button>
                    ))
                  ) : (
                    <span className="text-center appearance-none px-3 py-2 cursor-pointer hover:bg-gray-200 w-full text-grey-500">
                      No matching items found
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
      {/* LinkOrg */}
      <Modal
        key={`Upload-Modal-${id}}`}
        isOpen={isDropFile}
        setIsOpen={setIsDropFile}
        dialogTitle="Drag And Drop File"
        dialogDescription="Upload desired files"
        successButtonText="Upload"
        checkboxDescription="I want this sent to the client"
        onSuccess={() => {
          setDesignLoading(true);
          ordersAPI.uploadFile(id, FileID, checkbox).then(() => {
            setIsDropFile(false);
            handleOrderStatusChange();
          });
          ordersAPI.AdminOrderChangeRequest(
            id,
            checkbox ? 'Design sent to client' : undefined,
            [FileID],
          );
        }}
        checkbox={checkbox}
        setCheckbox={setCheckbox}
      >
        <div className="pb-8">
          <FileUploadComponent
            onFileSelected={file => {
              setDesignSizeError(false);
              setDesignFileError(false);
              setIsLoading(true);
              filesAPI
                .createAndUploadOrderFile(orgId, new File([file], file.name))
                .then(res => {
                  setFileID(res?.data?.data.id);
                })
                .catch(() => setDesignFileError(true))
                .finally(() => setIsLoading(false));
            }}
            fileFormat=".svg, .eps, .ai, .indd, .pdf"
            fileFormatMessage="(.svg, .eps, .ai, .indd, or .pdf only)"
            loading={isLoading}
            sizeError={designSizeError}
            fileError={designFileError}
            file={file}
            setFile={setFile}
            setSizeError={setDesignSizeError}
          />
        </div>
      </Modal>
      {/* Change Request */}
      <ModalNoSuccess
        isOpen={isChangeRequest}
        setIsOpen={setIsChangeRequest}
        dialogTitle="Change Request"
        dialogDescription="View comments and change request from clients"
        cancelButtonText="Close"
      >
        <div className="mt-4">
          {successDownloadChangeRequestFile !== '' && (
            <SuccessAlert message={successDownloadChangeRequestFile} />
          )}
          {errorDownloadChangeRequestFile && (
            <ErrorAlert message={MESSAGES.error.generic} />
          )}
        </div>
        <TextAreaMessage
          placeholder=""
          readOnly
          id="TextArea-changeRequest"
          message={changeRequest}
        />
        <div>
          {!!changeRequestFile?.length && (
            <p className=" text-sm font-medium text-gray-900 my-4">Files</p>
          )}

          {changeRequestFile?.map((item: any, index: number) => (
            <button
              className="text-brand-500 w-full"
              key={index}
              onClick={e => {
                filesAPI
                  .downloadFile(item.original_url)
                  .then((res: AxiosResponse) => {
                    const a = document.createElement('a');
                    a.href = window.URL.createObjectURL(new Blob([res.data]));
                    a.download = item.file.file_name;
                    a.click();

                    setSuccessDownloadChangeRequestFile(
                      `File ${item.file.file_name} downloaded successfully!`,
                    );
                  })
                  .catch((error: AxiosError) =>
                    setErrorDownloadChangeRequestFile(true),
                  );
              }}
            >
              <div className=" my-2 lg:flex hidden">
                {downloading ? (
                  <Spinner className="h-7 w-7" />
                ) : (
                  <DownloadIcon className="h-6 w-6" />
                )}
                <p className="ml-2 ">{item.name}</p>
              </div>
            </button>
          ))}
        </div>
      </ModalNoSuccess>

      <OrderDetails
        order={order}
        orderId={id}
        orgId={orgId}
        orderStage={orderStatus}
        isOpenOrderDetailModal={isOpenOrderDetailModal}
        setIsOpenOrderDetailModal={setIsOpenOrderDetailModal}
        setIsMoveOrder={setIsMoveOrder}
        orderNumber={orderNumber}
        isPriority={isPriority}
        handleClickLinkedOrg={handleClickLinkedOrg}
        orderAlternateEmail={alternate_email}
        orderAlternateEmailFlag={alternate_email_flag}
        handleOrderStatusChange={handleOrderStatusChange}
        tag={tag}
        customerName={customer_name}
        customerEmail={customer_email}
        orderStatusUrl={orderUrl}
      />
    </div>
  );
}
